import { useEffect } from 'react';
import type { FlowStepDefinition } from '../types/config';

/**
 * Registers window.beforeunload while currentStepIndex is before the step
 * matching targetStepKey. Browsers show a generic confirmation dialog; custom
 * messages are typically ignored for security reasons.
 */
const useUnloadWarningUntilStep = (
  flowSteps: FlowStepDefinition[],
  currentStepIndex: number,
  targetStepKey: string | undefined,
) => {
  useEffect(() => {
    if (!targetStepKey) {
      return undefined;
    }

    const targetIndex = flowSteps.findIndex((s) => s.key === targetStepKey);
    if (targetIndex === -1) {
      return undefined;
    }

    const shouldWarn = currentStepIndex < targetIndex;
    if (!shouldWarn) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent): string | void => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
      return 'Changes you made may not be saved.';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [flowSteps, currentStepIndex, targetStepKey]);
};

export default useUnloadWarningUntilStep;
